import { pushToDataLayer, trackEvent } from './gtm'

/**
 * Pre-built event helpers for common analytics actions.
 *
 * Usage:
 *   import { analytics } from '@repo/analytics'
 *   analytics.propertyView('prop-123', 'apartment', 'Cairo')
 *   analytics.contactOwner('prop-123', 'whatsapp')
 */
export const analytics = {
  /** Track a button click */
  buttonClick: (
    buttonId: string,
    buttonText?: string,
    section?: string,
  ) =>
    trackEvent({
      name: 'button_click',
      params: { button_id: buttonId, button_text: buttonText, section },
    }),

  /** Track a property detail view */
  propertyView: (
    propertyId: string,
    propertyType?: string,
    city?: string,
    propertyName?: string,
  ) =>
    trackEvent({
      name: 'property_view',
      params: {
        property_id: propertyId,
        property_type: propertyType,
        city,
        property_name: propertyName,
      },
    }),

  /** Track a search action */
  search: (
    searchTerm: string,
    filters?: string,
    resultsCount?: number,
  ) =>
    trackEvent({
      name: 'search',
      params: {
        search_term: searchTerm,
        filters,
        results_count: resultsCount,
      },
    }),

  /** Track when a user contacts a property owner */
  contactOwner: (
    propertyId: string,
    method: 'phone' | 'whatsapp' | 'email' | 'form',
  ) =>
    trackEvent({
      name: 'contact_owner',
      params: { property_id: propertyId, method },
    }),

  /** Track a form submission */
  formSubmit: (formId: string, formName: string, success: boolean) =>
    trackEvent({
      name: 'form_submit',
      params: { form_id: formId, form_name: formName, success },
    }),

  /** Track a CTA click */
  ctaClick: (ctaId: string, ctaText?: string, destination?: string) =>
    trackEvent({
      name: 'cta_click',
      params: { cta_id: ctaId, cta_text: ctaText, destination },
    }),

  /** Track a listing impression in search results */
  listingImpression: (
    propertyId: string,
    position?: number,
    listName?: string,
  ) =>
    trackEvent({
      name: 'listing_impression',
      params: {
        property_id: propertyId,
        position,
        list_name: listName,
      },
    }),

  /** Track booking start */
  bookingStart: (
    propertyId: string,
    checkIn?: string,
    checkOut?: string,
    propertyName?: string,
    guests?: number,
  ) =>
    trackEvent({
      name: 'booking_start',
      params: {
        property_id: propertyId,
        check_in: checkIn,
        check_out: checkOut,
        property_name: propertyName,
        guests,
      },
    }),

  /**
   * Track a completed booking as a GA4 `purchase` — the conversion / Key event.
   * Pushes the standard GA4 `ecommerce` object (read by GTM's "Send Ecommerce
   * data from Data Layer") so revenue AND item-level (per-property) reports
   * populate. `property_name` is also sent flat for non-ecommerce breakdowns.
   */
  purchase: (booking: {
    transactionId: string
    propertyId: string
    propertyName?: string
    propertyType?: string
    city?: string
    value: number
    currency: string
    /** Promo / coupon code applied (GA4 `coupon`) */
    coupon?: string
    /** Discount amount applied */
    discount?: number
    checkIn?: string
    checkOut?: string
    guests?: number
    nights?: number
  }) => {
    // Clear any previous ecommerce object so items don't merge across events.
    pushToDataLayer({ ecommerce: null })
    trackEvent({
      name: 'purchase',
      params: {
        property_name: booking.propertyName,
        check_in: booking.checkIn,
        check_out: booking.checkOut,
        guests: booking.guests,
        nights: booking.nights,
        ecommerce: {
          transaction_id: booking.transactionId,
          value: booking.value,
          currency: booking.currency,
          coupon: booking.coupon,
          items: [
            {
              item_id: booking.propertyId,
              item_name: booking.propertyName,
              item_category: booking.propertyType ?? booking.city,
              price: booking.value,
              quantity: 1,
              coupon: booking.coupon,
              discount: booking.discount,
            },
          ],
        },
      },
    })
  },

  /** Track sign up */
  signUp: (method: string) =>
    trackEvent({ name: 'sign_up', params: { method } }),

  /** Track login */
  login: (method: string) =>
    trackEvent({ name: 'login', params: { method } }),

  /** Track any custom event */
  custom: (
    eventName: string,
    params: Record<string, string | number | boolean | undefined>,
  ) => trackEvent({ name: eventName, params }),
} as const
